import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import AttachmentType from "../types/attachmentType";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface AttachmentTypeState {
  attachmentType: AttachmentType;
  attachmentTypes: AttachmentType[];
  attachmentTypeSearchResults?: SearchResults;
  attachmentTypeFilter: SearchParams;
  status: "idle" | "loading" | "failed";
}

const initialState: AttachmentTypeState = {
  attachmentType: {
    id: 0,
    name: "",
  },
  attachmentTypes: [],
  attachmentTypeFilter: defaultParams,
  status: "idle",
};

export const getAttachmentType = createAsyncThunk<AttachmentType, number>(
  "attachmentType/get",
  async (id: number) => {
    const response = await http.get<AttachmentType>(`attachmenttype/${id}`);
    return response.data;
  }
);

export const getAttachmentTypes = createAsyncThunk<AttachmentType[]>(
  "attachmentTypes/get/all",
  async () => {
    const response = await http.get<AttachmentType[]>(`attachmenttype/`);
    return response.data;
  }
);

export const postAttachmentType = createAsyncThunk<
  AttachmentType,
  AttachmentType
>("attachmentType/post", async (type: AttachmentType) => {
  const response = await http.post<AttachmentType>(`attachmenttype/`, type, {
    params: {
      successToast: true,
    },
  });
  return response.data;
});

export const editAttachmentType = createAsyncThunk<
  AttachmentType,
  AttachmentType
>("attachmentType/edit", async (type: AttachmentType) => {
  const response = await http.put<AttachmentType>(
    `attachmenttype/${type.id}`,
    type,
    {
      params: {
        successToast: true,
      },
    }
  );
  return response.data;
});

export const deleteAttachmentType = createAsyncThunk<AttachmentType, number>(
  "attachmentType/delete",
  async (id: number) => {
    const response = await http.delete<AttachmentType>(`attachmenttype/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchAttachmentTypes = createAsyncThunk<
  SearchResults,
  SearchParams
>("attachmentType/search", async (search: SearchParams) => {
  let qs = getSearchQueryString(search);
  const response = await http.get<SearchResults>(`attachmenttype/search?${qs}`);
  return response.data;
});

const attachmentTypeSlice = createSlice({
  name: "attachmentType",
  initialState,
  reducers: {
    setAttachmentTypeFilter(state, action) {
      state.attachmentTypeFilter = action.payload;
    },
    resetAttachmentType(state) {
      state.attachmentType = { ...initialState.attachmentType };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAttachmentType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAttachmentType.fulfilled, (state, action) => {
        state.status = "idle";
        state.attachmentType = action.payload;
      })
      .addCase(getAttachmentType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(getAttachmentTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getAttachmentTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.attachmentTypes = action.payload;
      })
      .addCase(getAttachmentTypes.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postAttachmentType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postAttachmentType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postAttachmentType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editAttachmentType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editAttachmentType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editAttachmentType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteAttachmentType.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteAttachmentType.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteAttachmentType.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchAttachmentTypes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchAttachmentTypes.fulfilled, (state, action) => {
        state.status = "idle";
        state.attachmentTypeSearchResults = action.payload;
      })
      .addCase(searchAttachmentTypes.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default attachmentTypeSlice.reducer;
export const { setAttachmentTypeFilter, resetAttachmentType } =
  attachmentTypeSlice.actions;
